import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import {
  AiEventType,
  StatusEvent,
  SessionErrorEvent,
  ConnectionErrorEvent,
} from "../../../types/ai-event";
import { useAiConnection } from "../../../connection/AiConnectionProvider";

interface StatusIndicatorProps {
  event: StatusEvent | SessionErrorEvent | ConnectionErrorEvent;
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({ event }) => {
  const theme = useTheme();
  const { retryNow } = useAiConnection();

  const isError =
    event.type === AiEventType.SESSION_ERROR ||
    event.type === AiEventType.CONNECTION_ERROR;
  const isConnError = event.type === AiEventType.CONNECTION_ERROR;

  const config = {
    [AiEventType.STATUS_RUNNING]: { label: "Cas is thinking...", color: theme.palette.info.main },
    [AiEventType.STATUS_IDLE]: { label: "Ready", color: theme.palette.success.main },
    [AiEventType.STATUS_TERMINATED]: { label: "Session ended", color: theme.palette.error.main },
    [AiEventType.SESSION_ERROR]: {
      label: (event as SessionErrorEvent).error ?? "Cas reported an error.",
      color: theme.palette.error.main,
    },
    [AiEventType.CONNECTION_ERROR]: {
      label: (event as ConnectionErrorEvent).error ?? "Connection error.",
      color: theme.palette.error.main,
    },
  }[event.type];

  // Non-error statuses keep the terse dot+caption row.
  if (!isError) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.5, px: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.color }} />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {config.label}
        </Typography>
      </Box>
    );
  }

  // Errors get a full-sentence inline block (Claude-Code style), with Retry for
  // connection errors.
  return (
    <Box
      data-testid="inline-error"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        my: 0.5,
        mx: 1,
        p: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.error.main}`,
        backgroundColor: `${theme.palette.error.main}14`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
          {isConnError ? "Connection error" : "Cas error"}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.primary, whiteSpace: "pre-wrap" }}>
        {config.label}
      </Typography>
      {isConnError && (event as ConnectionErrorEvent).retryable && (
        <Box>
          <Button size="small" variant="outlined" color="error" data-testid="conn-error-retry" onClick={() => retryNow()}>
            Retry
          </Button>
        </Box>
      )}
    </Box>
  );
};
